import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Camera, Crown, Gem, Pencil, Sparkles, Star } from 'lucide-react'
import {
  fetchWallet,
  getProfile,
  listPhotos,
  type Photo,
  type ProfilePayload,
  type VIPTierName,
  type WalletBalances,
} from '../lib/api'
import { useTelegramBackButton } from '../lib/telegram'
import { ProfileSkeleton } from '../components/Skeleton'
import { useUserStore } from '../store/userStore'

type PageState =
  | { status: 'loading' }
  | { status: 'ready'; profile: ProfilePayload | null; photos: Photo[]; wallet: WalletBalances | null }
  | { status: 'error' }

// Mirrors the backend's ageFromBirthdate (apps/api/feed_handlers.go) closely
// enough for display purposes: whole years since birthdate, accounting for
// whether this year's birthday has already passed.
function ageFromBirthdate(birthdate: string): number | null {
  const parsed = new Date(birthdate)
  if (Number.isNaN(parsed.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - parsed.getFullYear()
  const hadBirthdayThisYear =
    now.getMonth() > parsed.getMonth() ||
    (now.getMonth() === parsed.getMonth() && now.getDate() >= parsed.getDate())
  if (!hadBirthdayThisYear) age -= 1
  return age
}

// Mirrors BuyVIPPage's formatDate.
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Visual theme per purchasable tier — mirrors BuyVIPPage.tsx's TIER_THEME
// (kept as a separate, smaller Record here rather than importing that
// page's theme object, since this card only needs the gradient+icon, not
// the whole purchase-flow styling surface). "admin" is handled separately
// below, not a fourth entry here — see TierCard's tier normalization.
const TIER_CARD_THEME: Record<VIPTierName, { gradient: string; icon: typeof Crown; nameKey: string }> = {
  vip: { gradient: 'from-amber-400 to-orange-500', icon: Crown, nameKey: 'vip.tierNames.vip' },
  premium: { gradient: 'from-violet-500 to-purple-600', icon: Sparkles, nameKey: 'vip.tierNames.premium' },
  supreme: { gradient: 'from-rose-500 to-red-600', icon: Star, nameKey: 'vip.tierNames.supreme' },
}

// Tier status card — the only entry point into /buy-vip anywhere in the
// Mini App besides hitting a paywall (daily like limit, rewind). Without
// this, a user who never happens to hit those walls has no way to discover
// VIP/Premium/Supreme at all. Same card slot shows either "renew" (active)
// or "upgrade" (not active), matching the wallet card's icon+CTA layout
// below it. `tier` is `me.active_tier` verbatim — `null` (free) shows the
// generic VIP-flavored upsell (today's exact behavior); `"admin"` (a real
// admin with no live /actas override — see apps/api/actas.go's
// effectiveTier) is normalized to the VIP theme too, since there's no
// specific product tier to render for a non-purchase-derived "admin" status.
function TierCard({ tier, expiresAt, onAction }: { tier: VIPTierName | 'admin' | null; expiresAt: string | null; onAction: (tier: VIPTierName | null) => void }) {
  const { t } = useTranslation()
  const isActive = tier !== null
  const themeTier: VIPTierName = tier === 'admin' || tier === null ? 'vip' : tier
  const theme = TIER_CARD_THEME[themeTier]
  const Icon = theme.icon
  const tierName = t(theme.nameKey)

  return (
    <button
      onClick={() => onAction(tier === 'admin' ? null : tier)}
      className={`flex w-full items-center gap-3 rounded-xl bg-gradient-to-r ${theme.gradient} p-3 text-left`}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20">
        <Icon className="h-5 w-5 fill-current text-white" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-bold text-white">
          {isActive ? t('profile.tierActiveTitle', { tier: tierName }) : t('profile.vipUpgradeTitle')}
        </p>
        <p className="text-xs text-white/80">
          {isActive && expiresAt
            ? t('profile.tierActiveUntil', { date: formatDate(expiresAt) })
            : t('profile.vipUpgradeSubtitle')}
        </p>
      </div>
      <span className="shrink-0 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-semibold text-white">
        {isActive ? t('profile.vipRenew') : t('profile.vipUpgradeCta')}
      </span>
    </button>
  )
}

// One consumable balance card (Crush today). Turbo/Praise will join once
// those features exist (see docs/monetization-roadmap.md) — just add more
// calls alongside this one, the surrounding flex row already accommodates it.
function WalletCard({
  icon,
  colorClass,
  label,
  value,
  getMoreLabel,
  onGetMore,
}: {
  icon: ReactNode
  colorClass: string
  label: string
  value: number
  getMoreLabel: string
  onGetMore: () => void
}) {
  return (
    <div className={`flex w-28 shrink-0 flex-col gap-2 rounded-xl p-3 ${colorClass}`}>
      <span className="flex items-center gap-1 text-xs font-semibold uppercase text-tg-text/70">
        {icon}
        {label}
      </span>
      <span className="text-2xl font-bold text-tg-text">{value}</span>
      <button
        onClick={onGetMore}
        className="rounded-lg bg-tg-bg/60 px-2 py-1 text-[11px] font-medium text-tg-text active:opacity-70"
      >
        {getMoreLabel}
      </button>
    </div>
  )
}

export default function ProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { me } = useUserStore()
  const [state, setState] = useState<PageState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [profile, photos] = await Promise.all([getProfile(), listPhotos()])
        // Crush balance is shown to every caller now — GET /me/wallet has
        // never been server-gated (the balance itself is harmless to
        // reveal), and Crush can be purchased/spent by anyone.
        const wallet = await fetchWallet().catch(() => null)
        if (cancelled) return
        setState({ status: 'ready', profile, photos, wallet })
      } catch {
        if (!cancelled) setState({ status: 'error' })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  useTelegramBackButton(() => navigate('/swipe'))

  if (state.status === 'loading') {
    return <ProfileSkeleton />
  }

  if (state.status === 'error') {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-4 px-8 text-center">
        <p className="text-sm text-red-500">{t('common.genericError')}</p>
        <button
          onClick={() => navigate('/swipe')}
          className="rounded-xl bg-tg-secondary-bg px-6 py-3 font-medium text-tg-text"
        >
          {t('common.back')}
        </button>
      </div>
    )
  }

  const { profile, photos, wallet } = state
  const mainPhoto =
    photos.find((p) => p.is_main && p.status === 'approved' && p.url) ??
    photos.find((p) => p.status === 'approved' && p.url)
  const age = profile?.birthdate ? ageFromBirthdate(profile.birthdate) : null

  return (
    <div className="flex min-h-dvh flex-col bg-tg-bg">
      <div className="flex items-center gap-3 px-4 pt-3">
        <button onClick={() => navigate('/swipe')} className="text-sm font-medium text-tg-hint">
          ← {t('common.back')}
        </button>
        <span className="text-base font-semibold text-tg-text">{t('profile.title')}</span>
      </div>

      <div className="flex flex-col items-center gap-3 px-6 py-6">
        <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-tg-secondary-bg text-2xl font-semibold text-tg-text">
          {mainPhoto?.url ? (
            <img
              src={mainPhoto.url}
              alt={profile?.display_name ?? ''}
              className="h-full w-full object-cover"
            />
          ) : (
            (profile?.display_name ?? '?').charAt(0).toUpperCase()
          )}
        </div>
        <div className="text-center">
          <p className="flex items-center justify-center gap-1.5 text-xl font-semibold text-tg-text">
            {profile?.display_name}
            {age !== null && <span className="text-tg-hint"> · {age}</span>}
            {me?.crown_eligible && !me.crown_hidden && (
              <Crown
                className="h-4 w-4 shrink-0 fill-current text-amber-400"
                aria-label={t('swipeCard.crownBadge')}
              />
            )}
          </p>
          {profile?.city && <p className="text-sm text-tg-hint">{profile.city}</p>}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/onboarding?edit=true')}
            className="flex items-center gap-1.5 rounded-xl bg-tg-secondary-bg px-4 py-2 text-sm font-medium text-tg-text"
          >
            <Pencil className="h-4 w-4" />
            {t('profile.editProfile')}
          </button>
          <button
            onClick={() => navigate('/photos')}
            className="flex items-center gap-1.5 rounded-xl bg-tg-secondary-bg px-4 py-2 text-sm font-medium text-tg-text"
          >
            <Camera className="h-4 w-4" />
            {t('profile.managePhotos')}
          </button>
        </div>
      </div>

      {/* Tier status — the only entry point into /buy-vip besides hitting a
          paywall (daily like limit, rewind). See TierCard's doc comment. */}
      <div className="px-4 pb-4">
        <TierCard
          tier={me?.active_tier ?? null}
          expiresAt={me?.vip_expires_at ?? null}
          onAction={(tier) =>
            navigate(tier ? `/buy-vip?reason=renew&tier=${tier}` : '/buy-vip')
          }
        />
      </div>

      {/* Wallet — visible to everyone, same as the Crush swipe button and
          Rewind. See docs/monetization-roadmap.md. */}
      <div className="px-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-tg-hint">
          {t('profile.walletTitle')}
        </p>
        <div className="flex gap-3 overflow-x-auto">
          <WalletCard
            icon={<Gem className="h-3.5 w-3.5" />}
            colorClass="bg-cyan-500/10"
            label={t('profile.crush')}
            value={wallet?.crush ?? 0}
            getMoreLabel={t('profile.getMore')}
            onGetMore={() => navigate('/buy-crush')}
          />
        </div>
      </div>
    </div>
  )
}
